import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Sparkles, ShoppingBag } from "lucide-react";
import { api, inr, getSessionId } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { Reveal, Overline } from "@/components/Reveal";

const COMBO_PRICE = 299;
const REGULAR = 375;

export default function ComboBuilder() {
  const { refreshCart, setCartOpen } = useStore();
  const sessionId = getSessionId();
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data.products));
  }, []);

  const toggle = (id) => {
    setSelected((s) => {
      if (s.includes(id)) return s.filter((x) => x !== id);
      if (s.length >= 3) { toast.message("Combo is 3 jars — deselect one first"); return s; }
      return [...s, id];
    });
  };

  const addCombo = async () => {
    if (selected.length !== 3) return;
    setAdding(true);
    try {
      for (const id of selected) {
        await api.post("/cart/add", { session_id: sessionId, product_id: id, qty: 1 });
      }
      await refreshCart();
      localStorage.setItem("dfk_pending_coupon", "COMBO3");
      toast.success(`Combo added — 3 jars for ${inr(COMBO_PRICE)}`, {
        description: "COMBO3 applies automatically at checkout",
      });
      setSelected([]);
      setCartOpen(true);
    } finally {
      setAdding(false);
    }
  };

  return (
    <section className="relative overflow-hidden bg-forest py-24 lg:py-28" id="combo" data-testid="combo-builder-section">
      <div className="absolute inset-0 grain opacity-50" />
      <div className="absolute inset-0 spotlight" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <Reveal>
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Overline>Build Your Box</Overline>
              <h2 className="mt-3 font-display text-5xl leading-none tracking-tight text-cream sm:text-7xl lg:text-8xl">
                MIX & MATCH <span className="text-gold-gradient">ANY 3 JARS</span>
              </h2>
              <p className="mt-4 max-w-md text-white/55">
                Pick any three flavours, snack your way. One flat combo price — the more you love,
                the more you save.
              </p>
            </div>
            <div className="flex items-end gap-4 rounded-3xl glass px-6 py-4">
              <div>
                <div className="font-oswald text-xs uppercase tracking-[0.15em] text-white/50">Combo Price</div>
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-5xl text-gold">{inr(COMBO_PRICE)}</span>
                  <span className="text-lg text-white/30 line-through">{inr(REGULAR)}</span>
                </div>
              </div>
              <span className="mb-1 rounded-full bg-green-500/15 px-3 py-1 text-xs font-semibold text-green-400">
                SAVE {inr(REGULAR - COMBO_PRICE)}
              </span>
            </div>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-6">
          {products.map((p, i) => {
            const on = selected.includes(p.id);
            return (
              <motion.button key={p.id} onClick={() => toggle(p.id)}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: (i % 6) * 0.05 }}
                data-testid={`combo-select-${p.slug}`}
                className={`group relative overflow-hidden rounded-2xl border-2 bg-black/40 p-2 text-left transition-all ${
                  on ? "border-gold gold-glow" : "border-white/10 hover:border-white/30"
                }`}>
                <div className="relative aspect-square overflow-hidden rounded-xl">
                  <img src={p.image} alt={p.name} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  {on && (
                    <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full gold-gradient text-black">
                      <Check size={14} />
                    </span>
                  )}
                </div>
                <div className="mt-2 line-clamp-1 font-oswald text-[11px] uppercase tracking-wide text-cream">{p.name}</div>
                <div className="text-[10px]" style={{ color: p.accent }}>{p.flavour}</div>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-5 rounded-3xl border border-gold/25 bg-black/40 p-6 sm:flex-row">
          <div className="flex items-center gap-3">
            <Sparkles className="text-gold" />
            <span className="font-oswald text-lg uppercase tracking-wide text-cream">
              {selected.length}/3 selected
            </span>
            <div className="flex gap-1.5">
              {[0, 1, 2].map((i) => (
                <span key={i} className={`h-2 w-8 rounded-full ${i < selected.length ? "gold-gradient" : "bg-white/15"}`} />
              ))}
            </div>
          </div>
          <button onClick={addCombo} disabled={selected.length !== 3 || adding} data-testid="add-combo-btn"
            className="flex items-center gap-2 rounded-full gold-gradient px-8 py-4 font-oswald text-sm uppercase tracking-[0.15em] text-black transition-transform hover:scale-105 disabled:opacity-40">
            <ShoppingBag size={17} />
            {adding ? "Adding…" : `Add Combo — ${inr(COMBO_PRICE)}`}
          </button>
        </div>
      </div>
    </section>
  );
}
